import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import { checkPermission } from '@/utils/permission';
import { fetchCustomer } from '@/redux/actions/customerActions';
import { Card, Empty, Skeleton, Space, Tabs, Tag, Typography } from 'antd';
import { IdcardOutlined, UserOutlined } from '@ant-design/icons';
import CustomerAppointments from './Appointments/CustomerAppointments';
import OrthoPlan from './OrthoPlan/Index';
import TreatmentSessionIndex from './TreatmentSessions/TreatmentSessionIndex';
import CustomerFollowUps from './CustomerFollowUps/CustomerFollowUps';
import CustomerCareIndex from './CustomerCare/CustomerCareIndex';
import CustomerInfoTab from './CustomerInfoTab';
import ImageTab from './Images/ImageTab';
import MedicalRecordForm from './MedicalRecord/MedicalRecordForm';
import PurchasedServices from './PurchasedServices/PurchasedServices';
import CustomerServiceLaboCases from './LaboCases/CustomerServiceLaboCases';

const title = 'Chi tiết khách hàng';
const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/customers', breadcrumbName: 'Danh sách khách hàng' },
    { path: '#', breadcrumbName: title },
];

const { TabPane } = Tabs;
const { Text } = Typography;

export default function Index() {

    const location = useLocation();
    const params = useParams();
    const history = useHistory();
    const dispatch = useDispatch();

    const customer = useSelector((s) => s.customerReducer.detail);
    const [isLoading, setIsLoading] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    // Quyền
    const routeName = 'customer';
    const allowDetail = checkPermission(routeName, 'detail');

    // Lấy id: ưu tiên params => query => state hiện tại
    const query = useMemo(() => qs.parse(location.search, { ignoreQueryPrefix: true }), [location.search]);
    const resolvedId = useMemo(() => {
        return params.id || query.id || customer?.id || null;
    }, [params.id, query.id, customer?.id]);

    const loadData = useCallback((id) => {
        if (!id) return;
        window.scrollTo(0, 0);

        setIsLoading(true);

        dispatch(fetchCustomer(id))
            .finally(() => {
                setIsLoading(false);
                setIsFirstLoad(false);
            });
    }, [dispatch]);

    // Lần đầu hoặc khi id đổi thì fetch
    useEffect(() => {
        if (!allowDetail) return; // hoặc chuyển hướng nếu không có quyền
        loadData(resolvedId);
    }, [allowDetail, loadData, resolvedId]);

    // Nếu không có id => chuyển về danh sách
    useEffect(() => {
        if (!resolvedId) {
            // Tùy flow: có thể show message rồi back
            history.replace('/customers');
        }
    }, [resolvedId, history]);

    const onTabClick = (key) => {
        // Cập nhật state và URL khi tab thay đổi
        history.push({
            pathname: location.pathname,
            search: qs.stringify({ ...query, tabs: key }),
        });
    };

    // Helmet được chèn vào đây
    const pageTitle = customer?.code && customer?.full_name
        ? `${customer.code} - ${customer.full_name} | CHINGO DENTAL`
        : 'CHINGO DENTAL';

    return (
        <div>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <PageTitle routes={routes} title={title} />
            <div className='mt-3'>
                <Card
                    title={
                        <Space size={8} align="center">
                            {isLoading ? (
                                <span>Đang tải...</span>
                            ) : (
                                <>
                                    <UserOutlined />
                                    <span>{customer?.full_name}</span>

                                    {customer?.code && (
                                        <Tag color="blue">
                                            <Space size={4} align="center">
                                                <IdcardOutlined />
                                                <Text
                                                    copyable={{
                                                        text: customer.code,
                                                        tooltips: ['Sao chép mã', 'Đã sao chép!'],
                                                    }}
                                                >
                                                    {customer.code}
                                                </Text>
                                            </Space>
                                        </Tag>
                                    )}
                                </>
                            )}
                        </Space>
                    }
                    bodyStyle={{ paddingTop: 0 }}
                >
                    {isLoading && isFirstLoad ? (
                        <div style={{ padding: '40px 0', textAlign: 'center' }}>
                            <Skeleton active title={false} paragraph={{ rows: 6 }} />
                        </div>
                    ) : !customer ? (
                        <Empty description="Không tìm thấy khách hàng" />
                    ) : (
                        <div>
                            <Tabs
                                defaultActiveKey={query.tabs}
                                size='middle'
                                type="card"
                                destroyInactiveTabPane
                                tabBarStyle={{
                                    fontWeight: 500,
                                    color: '#1890ff',
                                    background: "white",
                                }}
                                className='customer-detail-tabs'
                                onChange={(value) => onTabClick(value)}
                            >
                                <TabPane tab="Thông tin khách hàng" key="info">
                                    <CustomerInfoTab data={customer} reloadData={() => loadData(resolvedId)} />
                                </TabPane>
                                <TabPane tab="Quá trình điều trị" key="treatment_sessions">
                                    <TreatmentSessionIndex customer={customer} />
                                </TabPane>
                                <TabPane tab="Hình ảnh" key="images">
                                    <ImageTab customer={customer} />
                                </TabPane>
                                <TabPane tab="Lịch hẹn" key="appointments">
                                    <CustomerAppointments appointments={customer?.appointments} customer={customer} />
                                </TabPane>
                                <TabPane tab="Bệnh án tổng quát" key="medical_records">
                                    <MedicalRecordForm key={`medical-records-${customer?.id}`} customer={customer} />
                                </TabPane>
                                <TabPane tab="Phác đồ chỉnh nha" key="ortho_plan">
                                    <OrthoPlan key={`ortho_plan-${customer?.id}`} customer={customer} />
                                </TabPane>
                                <TabPane tab="Telesale & Tư vấn" key="telesale_tuvan">
                                    <CustomerFollowUps customer={customer} key={`customer-care-${customer?.id}`} />
                                </TabPane>
                                <TabPane tab="Dịch vụ chốt & Thanh toán" key="purchased_services">
                                    <PurchasedServices customer={customer} key={`used-services-${customer?.id}`} />
                                </TabPane>
                                <TabPane tab="CSKH" key="cskh">
                                    <CustomerCareIndex customer={customer} />
                                </TabPane>
                                <TabPane tab="Labo" key="labo">
                                    <CustomerServiceLaboCases services={customer?.active_purchased_services || []} />
                                </TabPane>
                            </Tabs>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
